import { useId, useState } from 'react'
import { useTranslation } from '../../lib/i18n'
import {
  computeCost,
  contextFit,
  estimateTokens,
  MODEL_PRICING,
} from '../../lib/model-pricing'

/**
 * Fully client-side token + context-window estimator.
 *
 * Paste text -> a heuristic token estimate (chars/token, clearly flagged as an
 * estimate, not an exact tokenizer), a per-model context-window fill bar with a
 * fits / does not fit verdict, and an approximate one-shot cost (input only)
 * that links to the full cost calculator. Pure, deterministic and SSR-safe: the
 * default text is empty and identical on server and client, all browser-only
 * concerns live in event handlers, numbers use Intl in the active locale, and
 * every string comes from the dictionary so it renders natively in EN and DE.
 *
 * Mounted by the tool detail page via the `token-context-estimator` toolKey.
 */

export function TokenContextEstimator() {
  const { dict, locale, path } = useTranslation()
  const ui = dict.pages.toolWidgets.tokenEstimator

  const [text, setText] = useState('')
  const textareaId = useId()

  const number = new Intl.NumberFormat(locale)
  const percent = new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 1,
  })
  const currency = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  })

  const tokens = estimateTokens(text)
  const characters = text.length
  const words = text.trim() ? text.trim().split(/\s+/u).length : 0

  const costCalculatorHref = path('tool', {
    toolSlug: 'llm-cost-calculator',
  })

  return (
    <div className="tool-widget">
      <form className="tool-widget-form" onSubmit={(e) => e.preventDefault()}>
        <label className="tool-field" htmlFor={textareaId}>
          <span>{ui.textLabel}</span>
          <textarea
            id={textareaId}
            rows={8}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={ui.textPlaceholder}
            spellCheck={false}
          />
        </label>
      </form>

      <div className="tool-stat-row" aria-live="polite">
        <div className="tool-stat">
          <span className="tool-stat-value">{number.format(tokens)}</span>
          <span className="tool-stat-label">{ui.tokensLabel}</span>
        </div>
        <div className="tool-stat">
          <span className="tool-stat-value">{number.format(characters)}</span>
          <span className="tool-stat-label">{ui.charactersLabel}</span>
        </div>
        <div className="tool-stat">
          <span className="tool-stat-value">{number.format(words)}</span>
          <span className="tool-stat-label">{ui.wordsLabel}</span>
        </div>
      </div>

      <p className="tool-hint">{ui.estimateNote}</p>

      <div className="tool-context-list">
        <h3 className="tool-context-heading">{ui.fitHeading}</h3>
        {MODEL_PRICING.map((model) => {
          const fit = contextFit(tokens, model)
          const inputCost = computeCost(model, {
            inputTokens: tokens,
            outputTokens: 0,
            monthlyCalls: 1,
            promptCaching: false,
            batch: false,
          }).inputCostPerCall
          return (
            <div className="tool-context-item" key={model.id}>
              <div className="tool-context-top">
                <span className="tool-context-name">
                  {model.name}
                  <small>
                    {ui.windowOf.replace(
                      '{window}',
                      number.format(model.contextWindow),
                    )}
                  </small>
                </span>
                <span
                  className={`tool-context-verdict${
                    fit.fits ? ' is-fit' : ' is-over'
                  }`}
                >
                  {fit.fits ? ui.fits : ui.doesNotFit}
                </span>
              </div>
              <div
                className="tool-context-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(fit.fillFraction * 100)}
                aria-label={`${model.name}: ${percent.format(fit.fillFraction)}`}
              >
                <span
                  className={`tool-context-bar-fill${
                    fit.fits ? '' : ' is-over'
                  }`}
                  style={{ width: `${fit.fillFraction * 100}%` }}
                />
              </div>
              <div className="tool-context-meta">
                <span>{percent.format(fit.rawFraction)}</span>
                <span>
                  {ui.approxCost.replace(
                    '{cost}',
                    currency.format(inputCost),
                  )}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      <p className="tool-disclaimer">
        {ui.costNote}{' '}
        <a className="tool-inline-link" href={costCalculatorHref}>
          {ui.costLink}
        </a>
      </p>
    </div>
  )
}
